import React, { useContext } from 'react';
import { Alert, Descriptions, Spin } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostDeviceDetailsContext from '@totem/components/ubiquiti/hosts/details/devices/deviceDetails/ubiquitiHostDeviceDetailsContext';
import { UbiquitiDeviceDetailsPort } from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import LinkStatusIndicator from '@totem/components/ui_components/widgets/linkStatus/linkStatusIndicator';
import { formatBytes, formatDate } from '@totem/utilities/common';

// UniFi reports uptime as a raw second count with no accompanying formatted
// field, so it's broken down into a "3d 4h 12m" style label here.
const formatUptime = (uptimeSec: number) => {
  const days = Math.floor(uptimeSec / 86400);
  const hours = Math.floor((uptimeSec % 86400) / 3600);
  const minutes = Math.floor((uptimeSec % 3600) / 60);

  return [
    days > 0 ? `${days}d` : null,
    hours > 0 ? `${hours}h` : null,
    `${minutes}m`,
  ]
    .filter((part) => part !== null)
    .join(' ');
};

const UbiquitiHostDeviceDetailsPanel = () => {
  const { loading, error, deviceDetails } = useContext(
    UbiquitiHostDeviceDetailsContext,
  );

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Device details unavailable"
        description="No detailed data could be found for this device."
      />
    );
  }

  if (loading || deviceDetails === null) {
    return <Spin spinning={loading} />;
  }

  const { deviceDetails: details, statistics } = deviceDetails;

  // The API returns ports in alpha-numeric order (1, 10, 11, 2, 3, ...) —
  // sort numerically by index for display. `ports` is null rather than []
  // for devices with no port data (no omitempty tag in the Go source).
  const sortedPorts = [...(details.interfaces.ports ?? [])].sort(
    (portA, portB) => portA.idx - portB.idx,
  );

  const portColumns: ColumnProps<UbiquitiDeviceDetailsPort>[] = [
    {
      title: 'Port',
      dataIndex: 'idx',
      key: 'idx',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (_, port: UbiquitiDeviceDetailsPort) => (
        <LinkStatusIndicator LinkType="ethernet" LinkStatus={port.state} />
      ),
    },
    {
      title: 'Connector',
      dataIndex: 'connector',
      key: 'connector',
    },
    {
      title: 'Speed',
      dataIndex: 'speedMbps',
      key: 'speedMbps',
      render: (_, port: UbiquitiDeviceDetailsPort) =>
        `${port.speedMbps} / ${port.maxSpeedMbps} Mbps`,
    },
  ];

  return (
    <>
      <Descriptions column={3} size="small" bordered>
        <Descriptions.Item label="Adopted At">
          {formatDate(details.adoptedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Provisioned At">
          {formatDate(details.provisionedAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Configuration ID">
          {details.configurationId}
        </Descriptions.Item>
        <Descriptions.Item label="Uptime">
          {formatUptime(statistics.uptimeSec)}
        </Descriptions.Item>
        <Descriptions.Item label="CPU / Memory">
          {statistics.cpuUtilizationPct}% / {statistics.memoryUtilizationPct}%
        </Descriptions.Item>
        <Descriptions.Item label="Load Average (1 / 5 / 15m)">
          {statistics.loadAverage1Min} / {statistics.loadAverage5Min} /{' '}
          {statistics.loadAverage15Min}
        </Descriptions.Item>
        <Descriptions.Item label="Uplink Tx / Rx">
          {formatBytes(statistics.uplink.txRateBps)}/s /{' '}
          {formatBytes(statistics.uplink.rxRateBps)}/s
        </Descriptions.Item>
        <Descriptions.Item label="Last Heartbeat">
          {formatDate(statistics.lastHeartbeatAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Next Heartbeat">
          {formatDate(statistics.nextHeartbeatAt)}
        </Descriptions.Item>
      </Descriptions>
      <br />
      <IBTable<UbiquitiDeviceDetailsPort>
        showSorterTooltip
        columns={portColumns}
        dataSource={sortedPorts}
        pagination={false}
        rowKey={(port) => port.idx}
      />
    </>
  );
};

export default UbiquitiHostDeviceDetailsPanel;
